import { LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "wouter";

interface EmptyStateAction {
  label: string;
  href?: string;
  onClick?: () => void;
  variant?: "default" | "outline" | "ghost";
  icon?: LucideIcon;
}

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  actions?: EmptyStateAction[];
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actions = [],
  className,
  size = "md",
}: EmptyStateProps) {
  const iconSizes = {
    sm: "h-8 w-8",
    md: "h-12 w-12",
    lg: "h-16 w-16",
  };

  const paddingClasses = {
    sm: "py-8 px-4",
    md: "py-12 px-4",
    lg: "py-16 px-6",
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        paddingClasses[size],
        className
      )}
    >
      <div className="rounded-full bg-muted p-4 md:p-6 mb-4 transition-transform hover:scale-105">
        <Icon className={cn(iconSizes[size], "text-muted-foreground")} />
      </div>
      <h3 className="text-base md:text-lg font-semibold mb-2 max-w-md">
        {title}
      </h3>
      <p className="text-sm md:text-base text-muted-foreground max-w-sm mb-6">
        {description}
      </p>
      {actions.length > 0 && (
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center w-full max-w-md">
          {actions.map((action, index) => {
            const ActionIcon = action.icon;
            const buttonContent = (
              <>
                {ActionIcon && <ActionIcon className="h-4 w-4 mr-2" />}
                {action.label}
              </>
            );

            return action.href ? (
              <Link key={index} href={action.href}>
                <Button
                  variant={action.variant || "default"}
                  className="min-h-[44px] flex-1 sm:flex-initial"
                >
                  {buttonContent}
                </Button>
              </Link>
            ) : (
              <Button
                key={index}
                onClick={action.onClick}
                variant={action.variant || "default"}
                className="min-h-[44px] flex-1 sm:flex-initial"
              >
                {buttonContent}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
